import {
  AppointmentInput,
  CreateAppointmentInput,
  UpdateReceiptInput,
} from "@/domain/dtos/inputs";
import {
  Appointment,
  AppointmentRecurrencyDetail,
  Payout,
} from "@/domain/entities";
import { AppointmentMapper } from "@/domain/mappers";
import { AppointmentRepository } from "@/domain/repositories";
import { AppointmentDataSource } from "../datasources/appointment";
import { UpdateAppointmentRequest } from "../dtos/requests";
import {
  AppointmentRecurrencyResponse,
  AppointmentResponse,
} from "../dtos/responses";
import {
  AppointmentStatus,
  RecurrenciesVerification,
} from "@/domain/contracts";
import { RecurrenciesVerificationParams } from "@/domain/dtos/params";
import { CreateRescheduleRequestInput } from "@/domain/dtos/inputs/CreateRescheduleRequestInput";
import { BusinessRuleError } from "@/domain";
import { Order } from "@/infra/api/ApiQuery";

export class AppointmentRepoImpl implements AppointmentRepository {
  constructor(
    private readonly dataSource: AppointmentDataSource,
    private readonly appointmentMapper: AppointmentMapper<
      AppointmentResponse,
      UpdateAppointmentRequest,
      AppointmentRecurrencyResponse
    >,
  ) {}

  async getAppointments(query: {
    orderByDirection?: Order;
    user_id?: number;
    psychologist_id?: number;
    filter?: string;
    type?: string;
    status?: AppointmentStatus[];
  }): Promise<Appointment[]> {
    if (!query.user_id && !query.psychologist_id) {
      throw new BusinessRuleError("Campo ID de usuário/psiclólogo obrigatório");
    }
    const data = await this.dataSource.getAppointments(query);

    return data.map((elem) => this.appointmentMapper.toDomain(elem));
  }

  async getAppointmentById(id: number): Promise<Appointment> {
    const data = await this.dataSource.getAppointmentById(id);

    return this.appointmentMapper.toDomain(data);
  }

  async createAppointment(
    params: CreateAppointmentInput,
  ): Promise<Appointment> {
    const data = await this.dataSource.createAppointment(params);
    const appointment = await this.dataSource.getAppointmentById(data.id);
    return this.appointmentMapper.toDomain(appointment);
  }

  async updateAppointment(
    id: number,
    params: AppointmentInput,
  ): Promise<Appointment> {
    const request = this.appointmentMapper.toUpdateRequest(params);

    const data = await this.dataSource.updateAppointment(id, request);

    return this.appointmentMapper.toDomain(data);
  }

  async getRecurrencyById(id: number): Promise<AppointmentRecurrencyDetail> {
    const data = await this.dataSource.getRecurrencyById(id);

    return this.appointmentMapper.toRecurrencyDetail(data);
  }

  async cancelRecurrency(id: number): Promise<void> {
    await this.dataSource.cancelRecurrency(id);
  }

  updateReceipt(
    id: number,
    params: UpdateReceiptInput,
  ): Promise<{ message: string }> {
    return this.dataSource.updateReceipt(id, params);
  }

  recurrenciesVerification(
    params: RecurrenciesVerificationParams,
  ): Promise<RecurrenciesVerification> {
    return this.dataSource.recurrenciesVerification(params);
  }

  updateRescheduleRequest(
    id: number,
    params: { status: string },
  ): Promise<any> {
    return this.dataSource.updateRescheduleRequest(id, params);
  }

  createRescheduleRequest(params: CreateRescheduleRequestInput): Promise<any> {
    return this.dataSource.createRescheduleRequest(params);
  }

  createCallEvent(params: {
    appointment_id: number;
    event: string;
  }): Promise<any> {
    return this.dataSource.createCallEvent(params);
  }
}
